import { useMemo } from 'react';
import {
  useGetCustomersQuery,
  useGetMachinesQuery,
  useGetPlantsQuery,
} from '../../gql/generated';
import {
  EDGE_DEVICE_CONNECTION_STATUS_OPTIONS,
  EDGE_DEVICE_STATUS_OPTIONS,
  useMachineFilters,
} from '../../hooks/useMachineFilters';
import { SearchInput } from '../SearchInput';
import { MobileSearchableSelect } from '../MobileSearchableSelect';
import { STATUS_OPTIONS } from './StatusFilter';

/**
 * Mobile filters for machines page.
 * Dynamically generates filters from machine data - automatically includes new EDMS properties.
 */
export function MobileFilters() {
  const {
    filters,
    setFilter,
    setDisplayName,
    setSerial,
    setPartNumber,
    setCustomers,
    setPlants,
    setFamilies,
    setStatuses,
    setCountryCodes,
    setCountries,
    setCities,
    setStates,
  } = useMachineFilters();

  // Fetch data for filters
  const { data: customersData, loading: customersLoading } =
    useGetCustomersQuery({
      fetchPolicy: 'cache-and-network',
    });

  const { data: plantsData, loading: plantsLoading } = useGetPlantsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const { data: machinesData, loading: machinesLoading } = useGetMachinesQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Transform options - dynamically generated from the data
  const customerOptions = useMemo(
    () =>
      (customersData?.customers || []).map((c) => ({
        value: c.id,
        label: c.displayName || c.id,
      })),
    [customersData]
  );

  const plantOptions = useMemo(
    () =>
      (plantsData?.plants || []).map((p) => ({
        value: p.id,
        label: p.displayName || p.id,
      })),
    [plantsData]
  );

  // Dynamic options derived from machine data
  const familyOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machinesData?.machines?.rows
            .map((m) => m.family)
            .filter((v): v is string => !!v) || []
        )
      )
        .sort()
        .map((v) => ({ value: v, label: v })),
    [machinesData]
  );

  const countryCodeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machinesData?.machines?.rows
            .map((m) => m.address?.countryCode)
            .filter((v): v is string => !!v) || []
        )
      )
        .sort()
        .map((v) => ({ value: v, label: v })),
    [machinesData]
  );

  const countryOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machinesData?.machines?.rows
            .map((m) => m.address?.country)
            .filter((v): v is string => !!v) || []
        )
      )
        .sort()
        .map((v) => ({ value: v, label: v })),
    [machinesData]
  );

  const cityOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machinesData?.machines?.rows
            .map((m) => m.address?.city)
            .filter((v): v is string => !!v) || []
        )
      )
        .sort()
        .map((v) => ({ value: v, label: v })),
    [machinesData]
  );

  const stateOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machinesData?.machines?.rows
            .map((m) => m.address?.state)
            .filter((v): v is string => !!v) || []
        )
      )
        .sort()
        .map((v) => ({ value: v, label: v })),
    [machinesData]
  );

  return (
    <>
      {/* Search filters */}
      <MobileSearchFilter
        label='Display name'
        value={filters.displayName}
        onChange={setDisplayName}
        placeholder='All names'
      />

      <MobileSearchFilter
        label='Serial'
        value={filters.serial}
        onChange={setSerial}
        placeholder='All serials'
      />

      <MobileSearchFilter
        label='Part Number'
        value={filters.partNumber}
        onChange={setPartNumber}
        placeholder='All part numbers'
      />

      {/* Multi-select filters */}
      <MobileSelectFilter
        label='Customer'
        options={customerOptions}
        selectedValues={filters.customers}
        onChange={setCustomers}
        title='Customers'
        placeholder={customersLoading ? 'Loading...' : 'All customers'}
      />

      <MobileSelectFilter
        label='Plant'
        options={plantOptions}
        selectedValues={filters.plants}
        onChange={setPlants}
        title='Plants'
        placeholder={plantsLoading ? 'Loading...' : 'All plants'}
      />

      <MobileSelectFilter
        label='Family'
        options={familyOptions}
        selectedValues={filters.families}
        onChange={setFamilies}
        title='Families'
        placeholder={machinesLoading ? 'Loading...' : 'All families'}
      />

      <MobileSelectFilter
        label='Status'
        title='Statuses'
        options={STATUS_OPTIONS}
        selectedValues={filters.statuses}
        onChange={setStatuses}
        placeholder='All statuses'
      />

      <MobileSelectFilter
        label='Country Code'
        options={countryCodeOptions}
        selectedValues={filters.countryCodes}
        onChange={setCountryCodes}
        title='Country codes'
        placeholder={machinesLoading ? 'Loading...' : 'All country codes'}
      />

      <MobileSelectFilter
        label='Country'
        options={countryOptions}
        selectedValues={filters.countries}
        onChange={setCountries}
        title='Countries'
        placeholder={machinesLoading ? 'Loading...' : 'All countries'}
      />

      <MobileSelectFilter
        label='City'
        options={cityOptions}
        selectedValues={filters.cities}
        onChange={setCities}
        title='Cities'
        placeholder={machinesLoading ? 'Loading...' : 'All cities'}
      />

      <MobileSelectFilter
        label='State/Province'
        options={stateOptions}
        selectedValues={filters.states}
        onChange={setStates}
        title='States and provinces'
        placeholder={
          machinesLoading ? 'Loading...' : 'All states and provinces'
        }
      />

      <MobileSelectFilter
        label='Edge Device Provisioning'
        options={EDGE_DEVICE_STATUS_OPTIONS}
        selectedValues={filters.edgeDeviceStatuses}
        onChange={(values) => setFilter('edgeDeviceStatuses', values)}
        title='Provisioning states'
        placeholder='All provisioning states'
      />

      <MobileSelectFilter
        label='Edge Device Connection'
        options={EDGE_DEVICE_CONNECTION_STATUS_OPTIONS}
        selectedValues={filters.edgeDeviceConnectionStatuses}
        onChange={(values) => setFilter('edgeDeviceConnectionStatuses', values)}
        title='Connection states'
        placeholder='All connection states'
      />

      <MobileSearchFilter
        label='Edge Device ID (IoT)'
        value={filters.edgeDeviceGuid}
        onChange={(value) => setFilter('edgeDeviceGuid', value)}
        placeholder='All edge device IDs'
      />
    </>
  );
}

// Generic mobile search filter component
interface MobileSearchFilterProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

function MobileSearchFilter({
  label,
  value,
  onChange,
  placeholder,
}: MobileSearchFilterProps) {
  return (
    <div className='flex flex-col gap-1.5'>
      <label className='size-p2 text-primary'>{label}</label>
      <SearchInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-label={label}
      />
    </div>
  );
}

// Generic mobile select filter component
interface MobileSelectFilterProps {
  label: string;
  options: { value: string; label: string }[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  /** The heading of the options level. Defaults to label. */
  title?: string;
}

function MobileSelectFilter({
  label,
  title,
  options,
  selectedValues,
  onChange,
  placeholder,
}: MobileSelectFilterProps) {
  return (
    <div className='flex flex-col gap-1.5'>
      <span className='size-p2 text-primary'>{label}</span>
      <div className='w-full min-w-0'>
        <MobileSearchableSelect
          options={options}
          selectedValues={selectedValues}
          onSelectionChange={onChange}
          placeholder={placeholder || `All ${label.toLowerCase()}`}
          label={label}
          title={title}
        />
      </div>
    </div>
  );
}
